import { type MouseEvent, useEffect, useRef, useState } from "react"

import { cn } from "@/lib/utils"

/* Ported from imperative-website's AudioSamplePlayer, restyled onto the
   lp-* tokens: real call recordings in a minimal player, one tab per
   industry. */
export interface AudioSample {
  id: string
  label: string
  src: string
  scenario: string
}

const fmt = (s: number) => {
  if (!Number.isFinite(s)) return "–:––"
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, "0")}`
}

export function AudioSamplePlayer({ samples }: { samples: AudioSample[] }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState<number>(NaN)
  const [errored, setErrored] = useState(false)

  const sample = samples[active]

  // metadata may already be in by the time the effect runs
  useEffect(() => {
    const audio = audioRef.current
    if (audio && audio.readyState >= 1) setDuration(audio.duration)
  }, [active])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    // A rejected play() is a browser autoplay/interruption policy talking, not
    // something the visitor needs told about — the button just stays on "play".
    if (audio.paused) void audio.play().catch(() => undefined)
    else audio.pause()
  }

  const seek = (event: MouseEvent) => {
    const audio = audioRef.current
    const progress = progressRef.current
    if (
      !audio ||
      !progress ||
      !Number.isFinite(audio.duration) ||
      audio.duration <= 0
    )
      return
    const rect = progress.getBoundingClientRect()
    const ratio = (event.clientX - rect.left) / rect.width
    audio.currentTime = Math.min(Math.max(ratio, 0), 1) * audio.duration
  }

  const switchSample = (i: number) => {
    if (i === active) return
    audioRef.current?.pause()
    setActive(i)
    setPlaying(false)
    setCurrentTime(0)
    setDuration(NaN)
    setErrored(false)
  }

  const ratio =
    Number.isFinite(duration) && duration > 0 ? currentTime / duration : 0

  return (
    <div className="border-lp-fg/10 rounded-2xl border bg-white p-6 shadow-[0_24px_60px_rgba(23,23,23,0.18)] lg:p-7">
      <p className="text-lp-fg text-sm font-medium">Hear it take a call</p>
      <p className="text-lp-muted/70 mt-0.5 text-xs">
        Real-world scenarios the receptionist handles
      </p>

      {/* industry tabs */}
      <div className="bg-lp-surface mt-5 flex gap-1 rounded-lg p-1">
        {samples.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => switchSample(i)}
            className={cn(
              "flex-1 cursor-pointer rounded-md px-2 py-2 text-xs font-medium transition-colors duration-150",
              i === active
                ? "text-lp-fg bg-white shadow-[0_1px_2px_rgba(6,7,7,0.08)]"
                : "text-lp-muted hover:text-lp-fg hover:bg-white/60",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* transport */}
      <div className="mt-6 flex items-center gap-4">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause sample" : "Play sample"}
          className="bg-lp-fg flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full text-white transition-colors duration-100 hover:bg-black"
        >
          {playing ? (
            <svg viewBox="0 0 16 16" fill="currentColor" className="size-4">
              <path d="M4 2.5h2.6v11H4v-11Zm5.4 0H12v11H9.4v-11Z" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 16 16"
              fill="currentColor"
              className="ml-0.5 size-4"
            >
              <path d="M4.5 2.7a.6.6 0 0 1 .9-.5l8.1 5.3a.6.6 0 0 1 0 1L5.4 13.8a.6.6 0 0 1-.9-.5V2.7Z" />
            </svg>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <p className="text-lp-muted truncate text-xs">
            {errored ? "Sample coming soon" : sample.scenario}
          </p>
          {/* progress */}
          <div
            ref={progressRef}
            onClick={seek}
            className="bg-lp-surface mt-2.5 h-1.5 cursor-pointer overflow-hidden rounded-full"
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${ratio * 100}%`,
                background: "var(--lp-accent)",
              }}
            />
          </div>
          <div className="text-lp-muted/70 mt-1.5 flex justify-between text-xs tabular-nums">
            <span>{fmt(currentTime)}</span>
            <span>{errored ? "–:––" : fmt(duration)}</span>
          </div>
        </div>
      </div>

      <audio
        key={sample.src}
        ref={audioRef}
        preload="metadata"
        src={sample.src}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false)
          setCurrentTime(0)
        }}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onError={() => {
          setPlaying(false)
          setErrored(true)
        }}
      />
    </div>
  )
}
